/**
 * InsertExampleButton.tsx — przycisk „Wstaw przykład" wstawiający przykładowy SQL/snippet
 * do pola formularza. Renderowany OBOK pola (nie w środku `FormField` — ten klonuje
 * dokładnie jedno dziecko, wstrzykując `id`/`aria-*`, więc rodzeństwo psuje wstrzyknięcie).
 *
 * Wzorowany na `documents/DecreeSuggestControls.tsx` („Podpowiedz dekret") — wypełnia pole
 * przez setter podany przez rodzica (`onInsert`), nie dotyka DOM ani schowka. Rodzic mapuje
 * `onInsert` na swój setter stanu (`setSql`/`setSourceSql`/…), więc wstawienie przechodzi tą
 * samą ścieżką co pisanie ręczne (Save odblokowuje się normalnie).
 *
 * Nadpisanie: gdy pole jest puste — wstawia od razu; gdy niepuste — najpierw `ConfirmDialog`
 * (`variant="primary"`, akcja nie-destrukcyjna), wstawia dopiero po potwierdzeniu. Komponent
 * sam trzyma stan swojego dialogu.
 *
 * `example` MUSI być czystym tekstem do wstawienia (bez prefiksu „np. " — ten należy tylko do
 * wyświetlanego `placeholder`).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

import { Button } from './Button';
import { ConfirmDialog } from './ConfirmDialog';

export interface InsertExampleButtonProps {
  /** Aktualna treść pola — decyduje, czy trzeba potwierdzić nadpisanie. */
  currentValue: string;
  /** Przykład do wstawienia (czysty, bez prefiksu „np. "). */
  example: string;
  /** Wywoływane z tekstem przykładu — rodzic ustawia stan pola swoim setterem. */
  onInsert: (text: string) => void;
  /** Blokuje przycisk (np. gdy pole SQL jest zablokowane w trybie edycji). */
  disabled?: boolean;
  'data-testid'?: string;
}

export function InsertExampleButton({
  currentValue,
  example,
  onInsert,
  disabled = false,
  'data-testid': testId = 'insert-example-btn',
}: InsertExampleButtonProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  function handleClick() {
    if (currentValue.trim().length === 0) {
      onInsert(example);
      return;
    }
    setConfirmOpen(true);
  }

  function handleConfirm() {
    onInsert(example);
    setConfirmOpen(false);
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        disabled={disabled}
        leftIcon={<Sparkles className="h-3.5 w-3.5" />}
        data-testid={testId}
      >
        {t('common.insertExample')}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        variant="primary"
        title={t('common.insertExampleConfirmTitle')}
        message={t('common.insertExampleConfirmMessage')}
        confirmLabel={t('common.insertExample')}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
